import * as React from "react";
import { withTranslation, WithTranslation } from "react-i18next";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { observer } from "mobx-react";
import { I18N_NS } from "Config/i18n";
import { EditableTable, EditableColumnProps } from "Components/General/EditableTable";
import { Tabs, Switch } from "antd";
import { accessRightsStore } from "../Stores/AccessRightsStore";
import { AssigneeUserSelect, AssigneeGroupSelect } from "./AssigneeIdSelect";
import { AccessRightsSelect } from "./AccessRightsSelect";
import { stringSort } from "utils/defaultSort";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { Button } from "Components/General/Button";
import { InactiveAssigneeText, isInactiveAssignee } from "Components/General/InactiveAssigneeText";
import { AccessRightsUserTable } from "./AccessRightsUserTable";
import { notificationSuccess } from "utils/libHelpers/AntdNotification";
import { availableAccessRightsStore } from "../Stores/AvailableAccessRightsStore";
import { modal } from "Components/General/Modal/AntdModal";

interface AccessRightsProps extends WithTranslation, ModalComponentProps {
  folderId: number;
  isReadAccess: boolean;
}

interface AccessRightsState {
  tableView: boolean;
}

export const AccessRights = withTranslation(I18N_NS.PROJECT)(
  observer(
    class extends React.Component<AccessRightsProps, AccessRightsState> {
      constructor(props: AccessRightsProps) {
        super(props);
        this.state = {
          tableView: false,
        };
      }

      componentDidMount() {
        (async () => await accessRightsStore.init(this.props.folderId))();
      }

      componentWillUnmount() {
        accessRightsStore.reset();
      }

      toggleView = () => {
        this.setState({ tableView: !this.state.tableView });
      };

      handleDelete = async (id: number) => {
        await accessRightsStore.deleteRight(id);
      };

      copyAccessRights = async () => {
        try {
          // PAVE-3185
          if (accessRightsStore.usersRights.some((right) => right.removed)) {
            await new Promise((res, rej) => {
              modal.confirm({
                content: this.props.t(
                  // eslint-disable-next-line max-len
                  "Rights for users or groups that are no longer present in the project will be ignored, which may result in different access rights than expected."
                ),
                onOk: () => res(undefined),
                onCancel: () => rej(undefined),
              });
            });
          }
          const response = await accessRightsStore.copyAccessRights();
          notificationSuccess(this.props.t("Success"), response.stringValue);
        } catch (error) {
          throw error;
        }
      };

      render() {
        const { t, isReadAccess } = this.props;
        const { tableView } = this.state;
        const { data } = availableAccessRightsStore;
        const groupTableColumns: EditableColumnProps<API_FolderRightsEntry>[] = [
          {
            title: t("Name"),
            dataIndex: "assigneeId",
            editable: true,
            defaultSortOrder: "ascend",
            inputType: "custom",
            render: (text, record) => record.name,
            sorter: (a, b) => stringSort(a, b, "name"),
            customInput: AssigneeGroupSelect,
            inputProps: {
              placeholder: t("Select assignee"),
              style: { width: "100%" },
              optionFilterProp: "children",
              showSearch: true,
            },
          },
          {
            title: t("Access right"),
            width: 230,
            dataIndex: "accessLevel",
            editable: true,
            sorter: (a, b) => stringSort(a, b, "accessRightKey"),
            render: (text, record) => {
              const val = data.length && data.find((right) => right.key === record.accessRightKey);
              return val ? val.key : "";
            },
            inputType: "custom",
            customInput: AccessRightsSelect,
            inputProps: {
              placeholder: t("Select access right"),
              style: { width: "100%" },
            },
            formItemProps: {
              rules: [{ required: true, message: t("Please enter right") }],
            },
          },
        ];

        const userTableColumns: EditableColumnProps<API_FolderRightsEntry>[] = [
          {
            title: t("Name"),
            dataIndex: "assigneeId",
            editable: true,
            defaultSortOrder: "ascend",
            inputType: "custom",
            render: (text, record) => (
              <InactiveAssigneeText removed={record.removed} projectMembershipActive={record.projectMembershipActive}>
                {record.name}
              </InactiveAssigneeText>
            ),
            sorter: (a, b) => stringSort(a, b, "name"),
            customInput: AssigneeUserSelect,
            inputProps: {
              placeholder: t("Select assignee"),
              style: { width: "100%" },
              optionFilterProp: "children",
              showSearch: true,
            },
          },
          {
            title: t("Access right"),
            width: 230,
            dataIndex: "accessLevel",
            editable: true,
            render: (text, record) => {
              const val = data.length && data.find((right) => right.key === record.accessRightKey);

              return val ? <span>{val.key}</span> : "";
            },
            sorter: (a, b) => stringSort(a, b, "accessRightKey"),
            inputType: "custom",
            customInput: AccessRightsSelect,
            inputProps: {
              placeholder: t("Select access right"),
              style: { width: "100%" },
            },
          },
        ];

        return (
          <div>
            <div className="text-right mb-2">
              {t("Effective rights per user")}
              <Switch checked={tableView} onChange={this.toggleView} style={{ marginLeft: 5 }} />
            </div>
            {tableView ? (
              <AccessRightsUserTable />
            ) : (
              <>
                <Tabs
                  type="card"
                  items={[
                    {
                      key: "1",
                      label: t("Groups"),
                      children: (
                        <EditableTable<API_FolderRightsEntry>
                          name="docsGroupsARs"
                          rowIdKey="id"
                          rowKey="id"
                          loading={accessRightsStore.state === "LOADING"}
                          columns={groupTableColumns}
                          dataSource={accessRightsStore.groupRights.slice()}
                          rowEditable={false}
                          rowDeletable={!isReadAccess}
                          onDelete={this.handleDelete}
                          onAddRow={accessRightsStore.addRight}
                          showAddRow={!isReadAccess}
                        />
                      ),
                    },
                    {
                      key: "2",
                      label: t("Additional single users"),
                      children: (
                        <EditableTable<API_FolderRightsEntry>
                          name="docsUsersARs"
                          rowIdKey="id"
                          rowKey="id"
                          loading={accessRightsStore.state === "LOADING"}
                          columns={userTableColumns}
                          dataSource={accessRightsStore.usersRights.slice()}
                          rowEditable={false}
                          rowClassName={(record) => (isInactiveAssignee(record) ? "color-red" : undefined)}
                          rowDeletable={!isReadAccess}
                          onDelete={this.handleDelete}
                          onAddRow={accessRightsStore.addRight}
                          showAddRow={!isReadAccess}
                        />
                      ),
                    },
                  ]}
                />
                <ButtonsRow>
                  {!isReadAccess && (
                    <Button onClick={this.copyAccessRights} loading={accessRightsStore.copyState === "LOADING"}>
                      {t("Copy directory access rights to the next sublevel")}
                    </Button>
                  )}
                </ButtonsRow>
              </>
            )}
          </div>
        );
      }
    }
  )
);
